import { z } from 'zod';

// What a prop may be filled from instead of typed. Bound, a field stops being somewhere to write and
// becomes a window onto the shop: the words are the store's name, the number beside the bag is what the
// cart holds, the links are a menu kept under Navigation. A binding governs what a field *says* and
// never how it looks — every style control beside it stays live, which is the whole point of the split.
//
// The vocabulary is closed. A source the shop cannot answer draws as a blank on a live page, and a
// blank on a live page is indistinguishable from a bug.

/** A menu binding carries its handle after the colon, so one source covers every menu a store keeps. */
export const MENU_SOURCE_PREFIX = 'menu:';

/**
 * The facts the shell has already resolved by the time a page draws, so binding one costs no read.
 *
 * A menu is deliberately not here: it is fetched by handle and arrives late, so the element that binds
 * one owns the read and its own waiting state. Everything below is a value the page already holds.
 */
export const BIND_SOURCES = [
  'store.name', 'store.logo', 'store.copyright',
  'store.email', 'store.phone', 'store.address', 'store.emailLink', 'store.phoneLink', 'store.mapLink',
  'collection.title', 'collection.image', 'collection.href', 'collection.count', 'collection.description',
  'cart.count', 'cart.total',
  'page.title', 'page.body', 'search.title',
  'product.title', 'product.price', 'product.compareAtPrice', 'product.description', 'product.summary', 'product.image', 'product.href', 'product.hoverImage', 'product.type', 'product.badge',
  'product.stock', 'product.rating', 'product.ratingCount',
  'product.saleBadge', 'product.newBadge', 'product.soldOutBadge',
  'review.author', 'review.body', 'review.title', 'review.rating', 'review.verified', 'review.image',
  'review.productTitle', 'review.productHref', 'review.productImage'
] as const;

export const BindSourceEnum = z.enum(BIND_SOURCES);

/** `menu:` and a handle, spelled the way `menus.handle` is — lowercase words joined by single dashes. */
const MENU_SOURCE_RE = /^menu:[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const BindSourceSchema = z.union([
  BindSourceEnum,
  z.string().max(70).regex(MENU_SOURCE_RE, 'A menu source is "menu:" and a handle, e.g. menu:main-menu')
]);

/** How many of one element's props may be bound; far past anything the catalog declares. */
export const MAX_BINDINGS = 20;

export const ElementBindingsSchema = z
  .record(z.string().min(1).max(60), BindSourceSchema)
  .refine((map) => Object.keys(map).length <= MAX_BINDINGS, `An element may bind at most ${MAX_BINDINGS} props`);

export type BindSource = z.infer<typeof BindSourceSchema>;
export type ElementBindings = z.infer<typeof ElementBindingsSchema>;

// Reads the handle out of a menu source, or nothing when the source names one of the resolved facts.
export function menuHandleOf(source: string): string | null {
  return source.startsWith(MENU_SOURCE_PREFIX) ? source.slice(MENU_SOURCE_PREFIX.length) : null;
}
